import React, { useCallback, useEffect } from "react"

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection"
import { mergeRegister } from "@lexical/utils"
import { Box } from "@mos-cat/ds"
import {
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from "lexical"

import styles from "./styles.module.css"
import { $isTableOfContentNode } from "."

const indent = (tagName) => {
  if (tagName === "h2") {
    return styles.heading2
  } else if (tagName === "h3") {
    return styles.heading3
  }
}

export const TableOfContentComponent = ({ nodeKey, tableOfContentsData }) => {
  const [editor] = useLexicalComposerContext()
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey)

  const onDelete = useCallback(
    (event) => {
      event.preventDefault()
      if (isSelected && $isNodeSelection($getSelection())) {
        const node = $getNodeByKey(nodeKey)
        if ($isTableOfContentNode(node)) {
          node.remove()
          return true
        }
      }
      return false
    },
    [isSelected, nodeKey],
  )

  const scrollToNode = useCallback(
    (key) => {
      editor.getEditorState().read(() => {
        const domElement = editor.getElementByKey(key)

        if (domElement !== null) {
          domElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          })
        }
      })
    },
    [editor],
  )

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const pbElem = editor.getElementByKey(nodeKey)

          if (event.target === pbElem) {
            if (!event.shiftKey) {
              clearSelection()
            }
            setSelected(!isSelected)
            return true
          }

          return false
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
    )
  }, [clearSelection, editor, isSelected, nodeKey, onDelete, setSelected])

  useEffect(() => {
    const pbElem = editor.getElementByKey(nodeKey)
    if (pbElem !== null) {
      pbElem.className = isSelected ? styles.selected : ""
    }
  }, [editor, isSelected, nodeKey])

  return (
    <Box as="ul" className={styles.tableOfContents}>
      {tableOfContentsData.map(([key, text, tag], index) => {
        const trimmedText = text?.trim() || ""
        return (
          <>
            {!!text.length && (
              <Box
                width="fit-content"
                as="li"
                key={key}
                onClick={() => scrollToNode(key, index, editor)}
                role="button"
                className={indent(tag)}
                tabIndex={0}
              >
                {trimmedText.length > 100 ? trimmedText.substring(0, 100) + "..." : trimmedText}
              </Box>
            )}
          </>
        )
      })}
    </Box>
  )
}
